const get = require('lodash/get')
const isArray = require('lodash/isArray')

const createShipmentSnapshot = require('./shipment-create-snapshot')
const updateShipmentSnapshot = require('./shipment-update-snapshot')
const createFollowUpShipment = require('./shipment-create-follow-up')
const createBulkShipmentSnapshots = require('./shipment-bulk-create-snapshots')
const findShipment = require('./shipment-find')
const saveShipmentChanges = require('./shipment-save-changes')
const saveAdjustments = require('./shipment-save-adjustments')
const addShipmentComment = require('./shipment-add-comment')
const addAdjustmentReasons = require('./shipment-add-adjustment-reasons')
const addProductToShipment = require('./shipment-add-product')
const removeProductFromShipment = require('./shipment-remove-product')
const markShipmentVoid = require('./shipment-mark-void')
const findAllSurveysForSnapshot = require('./survey-find-all-for-snapshot')
const findAllSurveysForShipment = require('./survey-find-all-for-shipment')
const updateCRQuantities = require('./update-cr-quantities')
const updateCRQuantitiesStats = require('./update-cr-quantities-stats')
const { exportShipmentsData, exportShipments } = require('./sl-shipment-export')
const { exportLMDShipments } = require('./psm-shipment-export')
const { exportShipmentsListData, exportShipmentsList } = require('./sl-shipment-list-export')
const findSurvey = require('./survey-find')
const findAdjustments = require('./shipment-find-adjustments')
const createSurvey = require('./survey-create')
const { bulkSnapshotsFromSheet } = require('./shipment-bulk-snapshot-from-allocation-sheet')
const validateSheetLocations = require('./validate-sheet-locations.js')
const { getImportTemplate } = require('./shipment-get-import-template')
const { getOutboundExcelTemplate } = require('./shipment-get-outbound-template')
const { convertExcelOutboundShipmentToCsv } = require('./psm-outbound-shipment-excel-to-csv-converter')
const { importShipments, importShipmentsFromSheet } = require('./shipment-import')
const { reschedule } = require('./shipment-reschedule')
const setupShipmentApi = require('./setup')
const { EntityApi, ProxifiedEntityApi } = require('../common')
const wrap = require('../utils/wrap-api')
const ShipmentPouchAdapter = require('./data-access/shipment-pouch-adapter')
const DataShipmentAdapter = require('./data-access/data-shipment-adapter')
const ShipmentSnapshotAdapter = require('./data-access/shipment-snapshot-adapter')
const RouteCacheAdapter = require('./data-access/route-cache-adapter')
const getReturnsNote = require('./shipment-get-returns-note')
const { bulkTranslateShipmentProducts } = require('./tools/bulk-translate-shipment-products')
const { exportWareHouseFunderShipments } = require('./export-warehouse-funder-shipments')
const parseShipmentUploadData = require('./tools/parse-shipment-upload-data')
const psmShipmentUpload = require('./psm-shipment-upload')
const { fromRelationalModel } = require('./tools/from-relational-model')
const docsToShipmentRecords = require('./tools/docs-to-shipment-records')
const { createBatchProductResolver, resolveBatchProductSkusByBatchIds } = require('../batch/tools/batch-product-resolver')
const { listBatches } = require('../batch/api/batch-list')
const toShipmentId = require('./tools/to-shipment-id')
const { parse } = require('../tools/smart-id')
const { getUserStatus } = require('../order/tools/read/user')
const docToVanRecord = require('./tools/doc-to-van-record')
const pgRowsToShipmentDocs = require('./tools/pg-rows-to-shipment-docs')
const { applyPgOtpVerification } = pgRowsToShipmentDocs
const { readRouteCreatedAt, resolveRouteCreatedAt } = require('../routes/tools/route-created-at')
const { vanStatuses } = require('../van-data-models/van-status-model')
const isVanStatusValid = status => vanStatuses.includes(status)
const { calculateShipmentVolumeAndPrice } = require('./tools/calculate-shipment-volume-price')
const { calculateDistance, gpsValidation, addGpsValidationFailComment, captureHandoffLocation } = require('./shipment-gps-validation')
const bulkAdvanceSnapshots = require('./shipment-bulk-advance-snapshots')
const getExternalShipment = require('./get-external-shipment')
const getShipmentByNumber = require('./get-shipment-by-number')
const getByParentDocId = require('./shipment-get-by-parent-doc-id')
const getRawShipmentsById = require('./get-raw-shipments-by-id')
const { SHIPMENT_STATUS } = require('./constants')
const { OtpApi } = require('./otp.js')
const ShipmentPriceListApi = require('./shipment-price-list')
const { applyLMDUserFilters } = require('./tools/apply-lmd-user-filters')
const { collapseOriginsToRealWarehouses } = require('./tools/lmd-source-warehouses')
const { mergeFilterObjects } = require('../location/tools/filters')
const keyBy = require('lodash/keyBy')

// Normalizes a batch ID so the product and manufacturer parts are always lowercase,
// while preserving the original casing of the batchNo segment.
// LMD snapshots can store batch IDs with mixed-case product parts
// (e.g. product:GHSC-AID0276:manufacturer:chemonics:batchNo:ZEA25016A),
// but master-data always stores them lowercase.
function normalizeBatchId (batchId) {
  const batchNoIdx = batchId.indexOf(':batchNo:')
  if (batchNoIdx === -1) return batchId
  return batchId.slice(0, batchNoIdx).toLowerCase() + batchId.slice(batchNoIdx)
}

const DEFAULT_LMD_ORDERING = ['-created_at', 'shipment_id']
const LMD_SORT_COLUMN_MAP = {
  shipmentNo: 'shipment_no_sort',
  originName: 'origin_name_sort',
  destinationName: 'destination_name_sort',
  funderName: 'funder_name_sort',
  programName: 'program_name_sort',
  createdAt: 'created_at',
  status: 'status_sort'
}

function getLmdOrdering ({ sortBy, sortDirection }) {
  const normalizedSortBy = sortBy && LMD_SORT_COLUMN_MAP[sortBy]
  if (!normalizedSortBy) {
    return DEFAULT_LMD_ORDERING
  }

  const isDescending = String(sortDirection || 'DESC').toUpperCase() === 'DESC'
  return [
    isDescending ? `-${normalizedSortBy}` : normalizedSortBy,
    'shipment_id'
  ]
}

const rawShipmentMethods = {
  addComment: addShipmentComment,
  addAdjustmentReasons,
  createSnapshot: createShipmentSnapshot,
  updateSnapshot: updateShipmentSnapshot,
  createFollowUpShipment,
  createBulkSnapshots: createBulkShipmentSnapshots,
  // Renamed so they don't shadow the dispatching `find` / `findById` class
  // methods once wrap() assigns these onto the instance.
  findLegacy: findShipment,
  findByIdLegacy: findShipment.findById,
  findByIds: findShipment.findByIds,
  saveChanges: saveShipmentChanges,
  reschedule: reschedule,
  addProduct: addProductToShipment,
  removeProduct: removeProductFromShipment,
  markVoid: markShipmentVoid,
  bulkSnapshotsFromSheet,
  validateSheetLocations,
  getImportTemplate,
  getOutboundExcelTemplate,
  convertExcelOutboundShipmentToCsv,
  calculateDistance,
  gpsValidation,
  addGpsValidationFailComment,
  captureHandoffLocation,
  importShipmentsFromSheet,
  importShipments,
  slExport: exportShipments,
  slExportData: exportShipmentsData,
  exportLMDShipments: exportLMDShipments,
  slShipmentListExport: exportShipmentsList,
  slShipmentListExportData: exportShipmentsListData,
  getReturnsNote,
  updateCRQuantities,
  updateCRQuantitiesStats,
  bulkTranslateShipmentProducts,
  exportWareHouseFunderShipments,
  bulkAdvanceSnapshots,
  getExternalShipment,
  getShipmentByNumber,
  getRawShipmentsById,
  getByParentDocId
}

const rawAdjustmentMethods = {
  draft: saveShipmentChanges.draftAdjustments,
  submit: saveAdjustments,
  find: findAdjustments
}

const rawSurveyMethods = {
  create: createSurvey,
  findAllForSnapshot: findAllSurveysForSnapshot,
  find: findSurvey,
  findAllForShipment: findAllSurveysForShipment
}

const entityName = 'shipment'

const methodsNeedingProxy = [
  {
    methodName: 'listLastMileDeliveries',
    restAction: 'GET',
    cache: false
  },
  {
    methodName: 'getLastMileDeliveriesFilterValues',
    restAction: 'GET',
    cache: false
  },
  'updateRoute',
  // we choose create (aka. POST) for calculate volume because
  // with shipment ids we can easily exceed the URL parameter limit,
  {
    methodName: 'calculateVolume',
    restAction: 'create'
  },
  // counts-based volume calculation used as CouchDB fallback when postgres line tables are empty
  {
    methodName: 'calculateVolumeFromCounts',
    restAction: 'create'
  }
]

class ShipmentApi extends ProxifiedEntityApi {
  constructor (state, agaveAdapter, pgConnection, logger, mainApi) {
    const { user, shipmentsDb } = state
    const adapter = new ShipmentPouchAdapter(user, shipmentsDb)
    super(entityName, methodsNeedingProxy, !pgConnection, adapter, agaveAdapter)

    if (pgConnection) {
      this.dataShipmentAdapter = new DataShipmentAdapter(pgConnection, user, logger, { withGlobalAccessUser: state.withGlobalAccessUser })
      this.shipmentSnapshotAdapter = new ShipmentSnapshotAdapter(pgConnection, user.name, logger, { withGlobalAccessUser: state.withGlobalAccessUser })
      this.routeCache = new RouteCacheAdapter(pgConnection, logger)
      this.HAS_PG_ACCESS = true
    }
    this.state = state
    // Store user for use in methods
    this.user = user
    // TODO: remove this when all raw methods have been ported
    wrap(rawShipmentMethods, state, setupShipmentApi, null, this)
    this.adjustment = {}
    wrap(rawAdjustmentMethods, state, setupShipmentApi, null, this.adjustment)

    this.otp = new OtpApi({ shipmentsDb, agaveAdapter, pgConnection })
    this.mainApi = mainApi
    this.priceList = new ShipmentPriceListApi(mainApi, pgConnection)
  }

  async getBucketObject (params) {
    if (!this.agaveAdapter) {
      throw new Error('getBucketObject requires agaveAdapter')
    }
    return this.agaveAdapter.get('shipment/bucket-object', params)
  }

  async logToS3 (params) {
    if (!this.agaveAdapter) {
      throw new Error('logToS3 requires agaveAdapter')
    }
    return this.agaveAdapter.post('shipment/log-to-s3', params)
  }

  async getCentralWarehouses () {
    if (!this.dataShipmentAdapter) {
      throw new Error('getCentralWarehouses requires pgConnection')
    }
    return this.dataShipmentAdapter.getCentralWarehouses()
  }

  /**
   * Browser-side dispatch for `find`. Online-only users keep no local pouch,
   * so a by-id read goes to the agave `shipment/snapshot/:id` endpoint (which
   * calls findFromPg below). Everyone else keeps today's couch/pouch path.
   *
   * `includeOrderDate` is forwarded as a query param rather than rejected: the
   * delivery note and package note both ask for it, and the date it returns is
   * the price reference date those notes print with — see findFromPg.
   */
  async find (params, options = {}) {
    const readsFromAgave = this.agaveAdapter && this.state.dataSource('shipment') === 'agave'
    if (!readsFromAgave) {
      return this.findLegacy(params, options)
    }

    if (typeof params !== 'string') {
      // find-by-location has no agave endpoint yet. Falling through to the
      // legacy path would read a pouch that was never replicated and quietly
      // return [], which reads as "this location has no shipments".
      throw new Error('ShipmentApi.find: only find-by-id is available over agave; find by location is not covered yet')
    }
    if (options.pendingAdjustments || options.includeOrder) {
      throw new Error('ShipmentApi.find: pendingAdjustments/includeOrder are not supported over agave')
    }
    // Both of these change the entity docsToShipmentRecords builds —
    // `userAsDistributor` becomes `useLocationAsOrigin` and rewrites the origin
    // of a shipment the user did not send; `checkSnapShotLocation` changes which
    // snapshot wins. The endpoint takes neither, so dropping them would hand back
    // a differently-built shipment with no sign anything was ignored.
    if (options.userAsDistributor || options.checkSnapShotLocation) {
      throw new Error('ShipmentApi.find: userAsDistributor/checkSnapShotLocation are not supported over agave')
    }

    const endpoint = `shipment/snapshot/${encodeURIComponent(params)}`
    // Only passed when asked for: the adapter appends a trailing slash before
    // any query string, so the plain read keeps the exact URL it has today.
    return options.includeOrderDate
      ? this.agaveAdapter.get(endpoint, { includeOrderDate: true })
      : this.agaveAdapter.get(endpoint)
  }

  /**
   * Same dispatch as `find`, for the by-id callers that want the single
   * shipment rather than an array (the edit flow, package notes, backend
   * transforms). Without this an online-only user reads a pouch that was never
   * replicated and gets findById's "unexpected result" 404.
   */
  async findById (id, options = {}) {
    const readsFromAgave = this.agaveAdapter && this.state.dataSource('shipment') === 'agave'
    if (!readsFromAgave) {
      return this.findByIdLegacy(id, options)
    }

    const shipments = await this.find(id, options)
    if (shipments.length !== 1) {
      // Same shape of failure as the legacy findById, so callers that catch a
      // 404 keep working.
      const error = new Error(`ShipmentApi.findById: unexpected result for "${id}", found: ${shipments.length}, expected 1`)
      error.status = 404
      throw error
    }

    return shipments[0]
  }

  /**
   * `find(shipmentOrSnapshotId)` served from Postgres instead of the local
   * pouch / remote couch pair. Used directly by the agave
   * `GET /shipment/snapshot/:id` route, which online-only users reach through
   * the dataSource resolver.
   *
   * Returns the same entity shape as the couch path because it goes through
   * the same `docsToShipmentRecords` merge — the Postgres rows are turned back
   * into couch-shaped documents first (see pg-rows-to-shipment-docs).
   *
   * `includeOrderDate` is served here too, from `avocado.data_order` — the
   * delivery note and package note ask for it and print prices against it, so a
   * shipment without it is a note with prices from the wrong date. See
   * getOrderCreatedDateFromPg for how the snapshot is chosen.
   *
   * Deliberately not supported here, and thrown on rather than silently
   * degraded, so a server-side caller gets the same answer as the browser-side
   * dispatch in `find` above:
   * - `pendingAdjustments` — a draft adjustment is a `_local/` pouch document
   *   that never leaves the device, so Postgres cannot have it. Throws rather
   *   than quietly returning a shipment without the user's pending edits.
   * - `includeOrder` — the couch path builds this by reading the order as a
   *   shipment snapshot (`getRequest`) and diffing its counts against the
   *   shipment's, which also pulls in the destination location and its pack
   *   points. That is the SL retailer delivery-statement flow, and no
   *   online-only caller asks for it; porting the whole order-as-shipments read
   *   to Postgres is its own change. The `orderId` itself is present either way.
   *
   * One field deliberately differs from the couch path rather than throwing:
   * `otp.password` is absent. Couch has it because the driver's own device wrote
   * it after verifying; the Postgres column of that name is the secret the
   * recipient is told out of band and must not be served here.
   * `otp.hasKeyAndPassword` is derived from `verified` instead, so the meaning the
   * clients rely on is preserved — see applyPgOtpVerification.
   *
   * @param {string} id - a shipment id or a snapshot id
   * @param {object} [options] - docsToShipmentRecords options (user, checkSnapShotLocation, ...)
   * @returns {Promise<object[]>} array of 0 or 1 shipment entities, as find() does
   */
  async findFromPg (id, options = {}) {
    if (!this.shipmentSnapshotAdapter) {
      throw new Error('ShipmentApi.findFromPg called without a pgConnection')
    }
    if (options.pendingAdjustments) {
      throw new Error('ShipmentApi.findFromPg: pendingAdjustments is a local-only draft and has no Postgres equivalent')
    }
    if (options.includeOrder) {
      throw new Error('ShipmentApi.findFromPg: includeOrder is not supported, the order-as-shipments read is couch-only')
    }

    const shipmentId = toShipmentId(id)
    const locationId = get(this.state, 'user.location.id')

    const rows = await this.shipmentSnapshotAdapter.getShipmentDocRows(shipmentId)
    const docs = pgRowsToShipmentDocs(shipmentId, rows)

    // Same status gate as the couch path: statuses outside the van status model
    // (`confirmed`, `in-process`, ...) exist in Postgres but are not shipments
    // as far as this entity is concerned.
    const filtered = docs.filter(doc => {
      if (doc.type !== 'snapshot') return true
      const { status } = docToVanRecord(doc, locationId)
      return isVanStatusValid(status)
    })

    const [shipment] = docsToShipmentRecords(locationId, filtered, { user: this.user, ...options })
    if (!shipment) return []

    if (options.includeOrderDate) {
      // Read off the docs, not the entity, so this asks the same question the
      // couch path asks: the first snapshot that names an order at all.
      const { orderId: rawOrderId } = filtered.find(doc => doc.orderId) || {}
      const { orderId } = parse(rawOrderId || '')
      // No orderId to parse out is not a failure — RMS shipments carry a bare
      // SAP number with no `:orderId:` fragment, and the couch path skips the
      // order read for those too.
      if (orderId) {
        const orderCreatedDate = await this.getOrderCreatedDateFromPg(orderId)
        if (orderCreatedDate) {
          shipment.orderCreatedDate = orderCreatedDate
        }
      }
    }

    // The one place the rebuild-and-merge pattern cannot carry a field: the merge
    // infers "already authenticated" from a couch doc Postgres does not have, and
    // the column that would stand in for it is a secret we must not send. See
    // applyPgOtpVerification.
    return [applyPgOtpVerification(shipment, rows.otp)]
  }

  /**
   * The `orderCreatedDate` the couch path gets from `getOrder`, read from
   * `avocado.data_order` instead.
   *
   * Mirrors what makes that value on the couch side: the order entity's
   * `createdAt` is not the order's first snapshot, it is the snapshot at the
   * status this *user* reads the order at — `warehouse` for a warehouse user,
   * `new` for everyone else (docsToOrderEntity picks `snapshotsByStatus[status]`).
   * So the status filter here is not incidental; dropping it would hand back a
   * different date than the same user sees over couch.
   *
   * Where couch takes `statusSnapshots[0]`, this takes the earliest `created_at`
   * at that status — the same row whenever there is only one, and deterministic
   * when there is not.
   *
   * Best-effort on purpose, matching the couch path, which catches its own order
   * read and continues without the date: a shipment the user can otherwise see
   * should not become unreadable because its order is missing from Postgres.
   *
   * @param {string} orderId - a bare order id, already parsed out of the shipment's orderId
   * @returns {Promise<string|null>} an ISO date string, or null if there is none to serve
   */
  async getOrderCreatedDateFromPg (orderId) {
    const orderAdapter = get(this.mainApi, 'order.orderAdapter')
    if (!orderAdapter) {
      console.error('No pg order adapter to read the order date from, continuing without')
      return null
    }

    try {
      const rows = await orderAdapter.getOrder({ orderId })
      const status = getUserStatus(get(this.state, 'user') || this.user || {})
      const createdDates = (rows || [])
        .filter(row => row.status === status && row.created_at)
        .map(row => new Date(row.created_at).toJSON())
        .sort()

      return createdDates.length ? createdDates[0] : null
    } catch (e) {
      console.error('Error fetching order date for shipment, continuing without:', e)
      return null
    }
  }

  resolveShipmentPrices (shipments) {
    return this.priceList.resolveShipmentPrices(shipments)
  }

  decorateShipmentSnapshotsWithResolvedPrices (shipments) {
    if (this.agaveAdapter && !this.HAS_PG_ACCESS) {
      return this.agaveAdapter.create('shipment/decorate-with-prices', { shipments })
    }
    return this.priceList.decorateShipmentSnapshotsWithResolvedPrices(shipments)
  }

  async updateRoute ({ shipmentIds, routeId }) {
    if (routeId === undefined) {
      throw new Error(`Cannot move shipment to route: routeId is required parameter`)
    }
    if (!shipmentIds || !shipmentIds.length) {
      throw new Error(`Cannot move shipment to route: shipmentIds is required parameter or it can't be empty`)
    }

    // Read the route's creation date before the writes: the copy that goes onto
    // the couch docs has to be the same value Postgres would join to. The
    // routes api's own adapter is what reads data_route everywhere else.
    // A route Postgres does not have yet resolves to undefined, which leaves
    // the date on docs already on that route alone; anything else going wrong
    // with the read throws here, before a write, rather than stamping a null
    // over a date the docs may already carry.
    const routeCreatedAt = await readRouteCreatedAt(get(this.mainApi, 'routes'), routeId)

    // Update Postgres and CouchDB in parallel
    const postgresUpdate = this.dataShipmentAdapter.updateWhere(
      { filter: { shipment_id: shipmentIds } },
      { route_id: routeId })

    // Update CouchDB snapshots in parallel for offline driver access
    const couchdbUpdate = this.updateRouteInCouchDB(shipmentIds, routeId, routeCreatedAt).catch(err => {
      // Log error but don't fail the whole operation if CouchDB update fails
      // Postgres update is the source of truth, CouchDB sync will catch up via sync pipeline
      console.error('Error updating route in CouchDB:', err)
      return null
    })

    await Promise.all([postgresUpdate, couchdbUpdate])
    return postgresUpdate
  }

  /**
   * Update routeId in CouchDB snapshots for shipments.
   * This ensures drivers using offline access can see shipments with updated routes immediately.
   * @param {Array<string>} shipmentIds - Array of shipment IDs to update
   * @param {string} routeId - The route ID to assign
   * @param {string|null} [routeCreatedAt] - Creation date of that route, read
   *   from avocado.data_route. Travels with the route id so an offline device
   *   has the date without a route doc to read it from (issue #25744).
   *   `undefined` means the date could not be read, and each doc already on
   *   this route then keeps the one it carries.
   * @returns {Promise<void>}
   */
  async updateRouteInCouchDB (shipmentIds, routeId, routeCreatedAt) {
    if (!this.mainApi || !this.mainApi.state) {
      return // Skip if state is not available (e.g., in test environments)
    }

    const updateSnapshot = require('./shipment-update-snapshot')
    const { state } = this.mainApi

    // Get all snapshots for all shipments in parallel
    const snapshotPromises = shipmentIds.map(shipmentId =>
      state.dal.shipment.getAllDocsWithPrefix(state.shipmentsDb, shipmentId)
    )

    const snapshotResults = await Promise.all(snapshotPromises)

    // Update each snapshot with the new routeId, and collect affected order IDs
    const updateTasks = []
    const orderIds = new Set()
    for (const result of snapshotResults) {
      for (const row of result.rows || []) {
        const doc = row.doc
        if (doc && doc.type === 'snapshot') {
          if (doc.orderId) orderIds.add(doc.orderId)
          // Only update if the route, or the date it was created on, is
          // actually changing — a snapshot that predates routeCreatedAt keeps
          // its route id but still needs the date.
          const snapshotRouteCreatedAt = resolveRouteCreatedAt(doc, routeId, routeCreatedAt)
          if (doc.routeId !== routeId || (doc.routeCreatedAt || null) !== snapshotRouteCreatedAt) {
            updateTasks.push({
              id: doc._id,
              promise: updateSnapshot(state, {
                snapshotId: doc._id,
                counts: doc.counts,
                shipmentTypeId: doc.shipmentTypeId,
                planningType: doc.planningType,
                createdAt: doc.createdAt,
                createdBy: doc.createdBy,
                _rev: doc._rev,
                funderId: doc.funderId,
                programId: doc.programId,
                routeId, // Update routeId
                routeCreatedAt: snapshotRouteCreatedAt, // ... and the date that route was created on
                orderId: doc.orderId,
                externalShipmentId: doc.externalShipmentId
              })
            })
          }
        }
      }
    }

    const results = await Promise.allSettled(updateTasks.map(t => t.promise))
    const failures = []
    results.forEach((res, idx) => {
      if (res.status === 'rejected') {
        const failedId = updateTasks[idx] && updateTasks[idx].id
        // Log each failure with snapshot id for easier troubleshooting
        console.error(`Error updating snapshot ${failedId} in CouchDB:`, res.reason)
        failures.push(failedId || 'unknown')
      }
    })

    if (failures.length) {
      // Throw a summarized error; caller of updateRouteInCouchDB already handles/logs
      throw new Error(`Failed to update ${failures.length} snapshot(s) in CouchDB`)
    }

    // Update the routeId on the order documents so that future packed snapshots
    // created from the order doc (via moveShipmentStatus) inherit the correct routeId.
    if (state.orderDb && orderIds.size > 0) {
      await updateOrderDocsRouteInCouchDB(state.orderDb, [...orderIds], routeId, routeCreatedAt)
    }
  }

  async unassignRoute ({ shipmentIds }) {
    if (!shipmentIds || !shipmentIds.length) {
      throw new Error(`Cannot unassign route: shipmentIds is required parameter or it can't be empty`)
    }

    // Update PostgreSQL
    const postgresUpdate = this.dataShipmentAdapter.updateWhere(
      { filter: { shipment_id: shipmentIds } },
      { route_id: null })

    // Update CouchDB snapshots (mirrors updateRoute pattern). No route means no
    // route creation date either.
    const couchdbUpdate = this.updateRouteInCouchDB(shipmentIds, null, null).catch(err => {
      console.error('Error unassigning route in CouchDB:', err)
      return null
    })

    await Promise.all([postgresUpdate, couchdbUpdate])
  }

  /**
   * Returns the subset of the given route ids that still have at least one
   * shipment. Used to detect "emptied"/orphaned routes — a route loses all its
   * shipments when cancelled or when all its stops are moved away (issue #20244
   * / move-stops). Scoped to the caller's organisation via the secured connection.
   *
   * @param {string[]} routeIds - couchdb route ids (e.g. 'route:uuid')
   * @returns {Promise<string[]>} the ids that have shipments
   */
  async getRouteIdsWithShipments (routeIds) {
    const ids = (Array.isArray(routeIds) ? routeIds : [routeIds]).filter(Boolean)
    if (!ids.length || !this.dataShipmentAdapter) {
      return []
    }
    const { rows } = await this.dataShipmentAdapter.pgConnection.query({
      text: 'SELECT DISTINCT route_id FROM avocado.data_shipment WHERE route_id = ANY($1)',
      values: [ids]
    })
    return rows.map(r => r.route_id).filter(Boolean)
  }

  async findByRoutes ({ routeIds }) {
    const shipmentEntities = await this.adapter.find({selector: {routeId: {$in: routeIds}}})
    const shipmentSnapshots = shipmentEntities.map(doc => ({...doc, _id: doc.id}))
    const shipments = docsToShipmentRecords(undefined, shipmentSnapshots, {
      useLocationAsOrigin: true
    })
    return shipments
  }

  getExternalShipment ({ shipmentNumber }) {
    if (!shipmentNumber) {
      throw new Error('Missing shipmentNumber parameter')
    }
    return getExternalShipment(this, { shipmentNumber })
  }

  getShipmentByNumber ({ shipmentNumber }) {
    if (!shipmentNumber) {
      throw new Error('Missing shipmentNumber parameter')
    }
    return getShipmentByNumber(this, { shipmentNumber })
  }

  getRawShipmentsById ({ shipmentId }) {
    if (!shipmentId) {
      throw new Error('Missing shipmentId parameter')
    }
    return getRawShipmentsById(this, { shipmentId })
  }

  parseShipmentUploadData (shipmentRows, programId, funderId, options) {
    return parseShipmentUploadData(shipmentRows, this.mainApi.location, this.mainApi.configuration, programId, funderId, options)
  }

  async uploadShipments (formData, hasToAddRoutesToShipments, shouldSync = false) {
    formData.append('hasToAddRoutesToShipments', hasToAddRoutesToShipments ? 'true' : 'false')
    const uploadedShipments = await this.agaveAdapter.create('shipment/upload', formData)
    if (shouldSync) {
      await this.state.shipmentsDb.sync(this.state.shipmentsDb, this.state.shipmentsRemoteDb)
    }
    return uploadedShipments
  }

  shipmentUpload (funderShipments, routes, crypto) {
    return psmShipmentUpload(this.mainApi.state, {
      funderShipments,
      routes,
      crypto,
      routesApi: this.mainApi.routes,
      shipmentApi: this.mainApi.shipment
    })
  }

  /**
   * Proxified method (see ProxifiedEntityApi) to list last mile deliveries.
   * @param {Object} params - Method options.
   * @param {Object} [params.filters] - Filters to apply (route_id, status, origin_id, destination_id, program_id, funder_id, shipment_id, state, limit, offset, sort_by, sort_direction...). Empty array values mean "match nothing" (e.g. route_id: [] for drivers with no routes).
   * @param {boolean} [params.separateByCompletion=false] - Resolve with { incomplete, complete } split on status === 'received' instead of { shipments, total }.
   * @param {boolean} [params.addSnapshotsData=true] - Enrich shipments with snapshotDates, GPS validation, OTP, and driverName.
   * @param {boolean} [params.addGroupOrderId=false] - Get related orders' groupOrder id.
   * @param {boolean} [params.onlyCount=false] - Resolve with just the number of matching shipments instead of the shipment list.
   * @param {boolean} [params.applyUserFilters=false] - Merge in the user's default filters (funder/program/route scoping). Only called from the agave controller.
   * @param {boolean} [params.forDownload=false] - Use the download SQL path (requires created_at_gte/lte or created_at filter).
   * @param {boolean} [params.skipLines=false] - Skip fetching shipment lines/counts. Callers that only need shipment identity (id, origin, destination, funder, programId, createdAt, shipmentNo) should set this to avoid the lines query + payload.
   * @param {string} [params.deliveryType=last_mile_delivery] - 'last_mile_delivery' | 'from_warehouse' | 'all_types'. TODO: This is misleading, should rename this method to something less LMD-specific.
   * @returns {Promise<{shipments: Array, total: number} | {incomplete: Array, complete: Array} | number>} List of last mile deliveries, split by completion, or a bare count when onlyCount.
   */
  async listLastMileDeliveries ({
    filters = {},
    pageNumber = 1,
    separateByCompletion = false,
    addSnapshotsData = true, // Enrich shipments with snapshotDates, GPS validation, OTP, and driverName
    addGroupOrderId = false, // Get related orders' groupOrder id
    onlyCount = false, // Only return the count of the shipments matching the filter
    applyUserFilters = false, // to be only called from agave controller to merge with default user filters
    forDownload = false, // for lmd shipment download only
    skipLines = false, // Skip fetching shipment lines (counts) for callers that only need shipment identity
    deliveryType = DataShipmentAdapter.SHIPMENT_TYPE_LAST_MILE_DELIVERY // we account for interwarehouse shipments in addition to of LMD and also allow to get just all shipments, unfiltered. TODO: This is misleading, should rename this method to something less LMD-specific
  }) {
    const { sort_by: sortBy, sort_direction: sortDirection } = filters

    // clone filters but "state" property
    const processedFilters = this.applyUserFiltersIfNeeded({
      filters,
      applyUserFilters
    })

    // Manage the STATE property that is in fact part of the destination_id string. STATE meaning administrative boundary for a country
    const stateSQLExpression = 'substring(destination_id from \'state:([^:]+)\')'

    // create state filter based on destination_id field
    const stateFilter = filters.state
    if (stateFilter) {
      delete processedFilters.state
      processedFilters[stateSQLExpression] = stateFilter
    }

    // getList manages offsets separated from the rest of the filter
    const offset = filters.offset
    if (offset) {
      delete processedFilters.offset
    }

    // getList manages limits separated from the rest of the filter
    const limit = filters.limit
    if (limit) {
      delete processedFilters.limit
    }

    delete processedFilters.sort_by
    delete processedFilters.sort_direction

    const adapterFilter = { ...DataShipmentAdapter.shipmentTypeFilters[deliveryType] }

    // When the user supplies a source (a warehouse id, or the funder-split
    // virtual warehouse ids it expands to), the warehouse-origin type
    // restriction ('%:name:%') is redundant: the supplied sources are already
    // warehouse origin ids. Dropping it here keeps origin_id out of the AND
    // merge so the user's selection can be OR-matched (issue #23868). The
    // destination type restriction is preserved.
    if (processedFilters.origin_id) {
      delete adapterFilter.origin_id
    }

    const ordering = getLmdOrdering({ sortBy, sortDirection })
    // When forDownload=true, we need to pass filters with state included, but getLastMileDeliveriesShipmentsForDownload
    // expects state as a plain string (not the SQL expression), so we merge filters with processedFilters to get
    // all processed filters, but keep state from the original filters if it exists.
    // When forDownload=false, merge adapterFilter with processedFilters.
    let mergedFilters
    if (forDownload) {
      // For download, we need state as a plain string (not SQL expression)
      // Extract state from SQL expression if it exists, or use filters.state
      const stateValue = stateFilter || processedFilters[stateSQLExpression] || filters.state

      // Merge processedFilters with filters, ensuring state is preserved as plain string
      // CRITICAL: Preserve route_id: [] from processedFilters if it exists (for drivers with no routes)
      // SECURITY: Always enforce empty array for drivers with no routes, even if user tries to pass route_id
      // This prevents drivers from bypassing the restriction by passing route_id in filters
      const preserveRouteId = Array.isArray(processedFilters.route_id) && processedFilters.route_id.length === 0
      mergedFilters = { ...processedFilters, ...filters }

      // Always enforce route_id: [] for drivers with no routes (security: prevent bypass attempts)
      if (preserveRouteId) {
        mergedFilters.route_id = []
      }

      // Remove the SQL expression version of state if it exists
      if (mergedFilters[stateSQLExpression]) {
        delete mergedFilters[stateSQLExpression]
      }

      // Ensure state is set as plain string for getLastMileDeliveriesShipmentsForDownload
      if (stateValue) {
        mergedFilters.state = stateValue
      }
    } else {
      mergedFilters = mergeFilterObjects(processedFilters, adapterFilter)
    }

    const result = forDownload ? await this.dataShipmentAdapter.getLastMileDeliveriesShipmentsForDownload(mergedFilters) : await this.dataShipmentAdapter.getList({
      ordering,
      filter: mergedFilters,
      limit,
      offset,
      addGroupOrderId,
      ...(skipLines
        ? {
          getRelated: {} // Override the adapter's default lines relation so the shipmentline query is skipped
        }
        : {}
      ),
      ...(onlyCount
        ? {
          skipCount: false,
          skipData: true
        }
        : {}
      )
    })

    if (onlyCount) {
      return result.count
    }

    const rows = forDownload ? result : result.results
    const rdsShipments = rows.map(row => {
      const shipment = fromRelationalModel({ shipment: row })
      if (addGroupOrderId) { // add group id to the built entity
        shipment.orderGroupId = get(row, 'order[0].group_id')
      }
      return shipment
    })

    if (addSnapshotsData && !forDownload) {
      const shipmentIds = rows.map(row => row.shipment_id)
      const snapshotData = this.shipmentSnapshotAdapter
        ? await this.shipmentSnapshotAdapter.getLmdSnapshotData(shipmentIds)
        : await this.getSnapshotDataForShipments(rows)

      // Index by id so the enrichment loop is O(N) instead of O(N^2).
      const snapshotById = new Map(snapshotData.map(item => [item.id, item]))
      rdsShipments.forEach(rdsShipment => {
        const match = snapshotById.get(rdsShipment.id)
        if (match) {
          rdsShipment.snapshotDates = match.snapshotDates
          rdsShipment.gpsValidationFailComment = match.gpsValidationFailComment
          rdsShipment.isGpsAuthenticated = match.isGpsAuthenticated
          rdsShipment.otp = match.otp
          rdsShipment.driverName = match.driverName
          rdsShipment.history = match.history
          rdsShipment.orderId = match.orderId
          if (match.collector) {
            rdsShipment.collector = match.collector
            rdsShipment.isSelfPickup = true
          }
        }
      })
    }

    if (forDownload) {
      return { shipments: rdsShipments }
    }

    if (!separateByCompletion) {
      return { shipments: rdsShipments, total: result.count }
    } else {
      return rdsShipments.reduce((acc, shipment) => {
        if (shipment.status === SHIPMENT_STATUS.RECEIVED) {
          acc.complete.push(shipment)
        } else {
          acc.incomplete.push(shipment)
        }
        return acc
      }, {incomplete: [], complete: []})
    }
  }

  /**
   * @deprecated Fallback for offline/PouchDB mode. Use shipmentSnapshotAdapter.getLmdSnapshotData() instead.
   * Retrieve and normalize snapshot data for a list of pg retrieved shipments.
   *
   * @async
   * @param {Array<{ shipment_id: string }>} shipments
   *   Array of shipment descriptor objects. Each object must contain a shipment_id property
   *   used as the prefix when fetching snapshot documents.
   *
   * @returns {Promise<Array<Object>>}
   *   Promise resolving to an array of normalized shipment record objects produced by docsToShipmentRecords.
   *
   * @throws {Error}
   *   Propagates any errors thrown by the underlying DAL calls or docsToShipmentRecords transformation.
   */
  async getSnapshotDataForShipments (shipments) {
    // let's get additional data from CouchDB
    const shipmentSnapshotsPromises = shipments.map(({shipment_id: shipmentId}) => this.mainApi.state.dal.shipment.getAllDocsWithPrefix(this.mainApi.state.shipmentsDb, shipmentId))
    const shipmentSnapshotsData = await Promise.all(shipmentSnapshotsPromises)

    // get a plain simple array of snapshots to feed docsToShipmentRecords
    const shipmentSnapshots = []
    shipmentSnapshotsData.forEach(item => {
      item.rows.forEach(row => {
        shipmentSnapshots.push(row.doc)
      })
    })
    return docsToShipmentRecords(undefined, shipmentSnapshots, {useLocationAsOrigin: true})
  }

  // TODO - TECH DEBT
  // At some point this function should be made PG only.
  // The right time would be when more data is available directly from PG, like:
  // - snapshots with batch level details
  // - master data that includes batch details
  // This would avoid multiple calls to CouchDB and PG and we could get this all done
  // in one single PG query.
  async getShipmentsForSftpOutboundFile (routeId, { alias = 'none' } = {}) {
    const route = await this.mainApi.routes.pgRouteViewDataAdapter.getOne(routeId, {
      whereCondition: 'couchdb_id'
    })
    if (!route) {
      throw new Error(`Route with id ${routeId} not found`)
    }

    const couchShipmentSnapshots = await this.getSnapshotDataForShipments(route.shipments)

    const { shipmentContent, batchIds, locationIds } = route.shipments.reduce(
      (acc, pgShipment) => {
        if (pgShipment.origin_id) acc.locationIds.add(pgShipment.origin_id)
        if (pgShipment.destination_id) acc.locationIds.add(pgShipment.destination_id)

        const couchShipment = couchShipmentSnapshots.find(
          snapshot => snapshot.id === pgShipment.shipment_id
        )

        if (!couchShipment) {
          console.log('No snapshot found for shipment:', pgShipment.shipment_id)
          return acc
        }

        const normalizedCounts = {}
        Object.keys(couchShipment.counts || []).forEach(batchId => {
          const normalizedId = normalizeBatchId(batchId)
          acc.batchIds.add(normalizedId)
          normalizedCounts[normalizedId] = couchShipment.counts[batchId]
        })

        return {
          ...acc,
          shipmentContent: {
            ...acc.shipmentContent,
            [pgShipment.id]: normalizedCounts
          }
        }
      },
      { shipmentContent: {}, batchIds: new Set(), locationIds: new Set() }
    )

    const locations = await this.mainApi.location.getByIds(Array.from(locationIds))
    const funders = await this.mainApi.funders.list({ unfiltered: false })

    const batchesResult = await this.state.masterDataDB.find({
      selector: {
        $and: [
          { type: { $eq: 'batch' } },
          { _id: { $in: Array.from(batchIds) } }
        ]
      },
      limit: Number.MAX_SAFE_INTEGER,
      fields: ['_id', 'manufacturer', 'expiry', 'alias']
    })

    const batchesByNormalizedId = keyBy(batchesResult.docs, '_id')

    // Load the product behind each batch so the outbound file can emit the
    // human-readable ItemDescription (and the native product code as the
    // ItemName fallback when alias === 'none'). Aliased batches resolve their
    // product via the alias code they carry; batches without that alias hold
    // the native product id in their own _id (product:<id>:manufacturer:...).
    const extractProductId = batchId => {
      const match = batchId.match(/^(product:[^:]+):manufacturer:/)
      return match ? match[1] : null
    }

    const aliasedBatches = []
    const nativeBatches = []
    for (const batch of batchesResult.docs) {
      if (alias !== 'none' && get(batch, `alias.${alias}`)) {
        aliasedBatches.push(batch)
      } else {
        nativeBatches.push(batch)
      }
    }

    const [aliasedProducts, nativeProducts] = await Promise.all([
      aliasedBatches.length
        ? this.mainApi.product.getByIds(
          aliasedBatches.map(batch => `product:${batch.alias[alias]}`),
          { resolveAlias: { [alias]: true } }
        )
        : [],
      nativeBatches.length
        ? this.mainApi.product.getByIds(
          nativeBatches.map(batch => extractProductId(batch._id)).filter(Boolean)
        )
        : []
    ])

    // Key the products by batch _id so the file generator can look each one up
    // directly. Aliased batches are matched back through the alias code, native
    // batches through the product id embedded in their _id.
    const productByAliasCode = aliasedProducts.reduce((acc, product) => {
      (get(product, `alias.${alias}`) || []).forEach(code => {
        acc[code.toUpperCase()] = product
      })
      return acc
    }, {})
    const nativeProductsById = keyBy(nativeProducts, '_id')

    const productsByBatchId = {}
    for (const batch of aliasedBatches) {
      const product = productByAliasCode[batch.alias[alias].toUpperCase()]
      if (product) productsByBatchId[batch._id] = product
    }
    for (const batch of nativeBatches) {
      const product = nativeProductsById[extractProductId(batch._id)]
      if (product) productsByBatchId[batch._id] = product
    }

    return {
      route,
      shipmentContent,
      batches: batchesByNormalizedId,
      locations: keyBy(locations, '_id'),
      products: productsByBatchId,
      funders: keyBy(funders, '_id')
    }
  }

  /**
   * Proxified method (see ProxifiedEntityApi) to get last mile deliveries filter values for a given date.
   * @param {Date} startDate - Date to use as a startint created_at date for the deliveries being taken into account.
   * @returns json object containing filtering values in the form:
   * {
   *    field1: [
   *       value1,
   *       value2,
   *       value3,
   *       ...
   *       ],
   *    field2: [
   *       value4,
   *       value5,
   *       value6,
   *       ...
   *       ],
   *    ...
   * }
   */
  async getLastMileDeliveriesFilterValues ({
    filters = {},
    applyUserFilters = false // to be only called from agave controller to merge with default user filters
  }) {
    const processedFilters = this.applyUserFiltersIfNeeded({
      filters,
      applyUserFilters
    })

    const result = await this.dataShipmentAdapter.getLastMileDeliveriesFilterValues(processedFilters)
    const values = get(result, 'rows[0].values', {})

    // The Source filter lists only real (physical) warehouses: funder-split
    // virtual copies are collapsed onto their parent warehouse (issue #23868).
    // The selection is resolved back to its real warehouse id in
    // applyLMDUserFilters, whose substring match covers all copies.
    if (Array.isArray(values.origin_id)) {
      values.origin_id = collapseOriginsToRealWarehouses(values.origin_id)
    }

    return values
  }

  /**
   * Proxified method (see ProxifiedEntityApi) to calculate shipment volume reading the data from Postgres.
   * @param {Object} params - Contains shipmentId.
   * @returns {Object} Object containing totalVolume and totalPrice.
   */
  async calculateVolume ({ shipmentIds, batchProductSkuById = null, preservePrecision = false } = {}) {
    if (!shipmentIds || !isArray(shipmentIds)) {
      throw new Error('shipmentIds required to get volume')
    }
    if (shipmentIds.length === 0) {
      return []
    }
    const resolvedBatchProductSkuById = batchProductSkuById === null && this.dataShipmentAdapter
      ? await this.resolveBatchProductSkusForShipments(shipmentIds)
      : batchProductSkuById
    return this.dataShipmentAdapter.getVolume(shipmentIds, {
      batchProductSkuById: resolvedBatchProductSkuById || {},
      preservePrecision
    })
  }

  async getShipmentBatchStrings (shipmentIds) {
    if (!shipmentIds || !shipmentIds.length) return []
    try {
      return await this.dataShipmentAdapter.getBatchStringsForShipments(shipmentIds)
    } catch (e) {
      return []
    }
  }

  // TODO: move to the pg adapter once the batch alias is available in Postgres.
  async resolveBatchProductSkusFromBatchIds (batchIds) {
    try {
      return await resolveBatchProductSkusByBatchIds(batchIds, (ids) => listBatches(this.state, { ids }))
    } catch (e) {
      return {}
    }
  }

  async resolveBatchProductSkusForShipments (shipmentIds) {
    if (!shipmentIds || !shipmentIds.length) return {}
    const batchStrings = await this.getShipmentBatchStrings(shipmentIds)
    return this.resolveBatchProductSkusFromBatchIds(batchStrings)
  }

  /**
   * Proxified method. Computes shipment volumes from CouchDB-sourced batch counts.
   * Used as a fallback when postgres line tables (data_shipmentline / data_shipmentsnapshotline)
   * are empty, e.g. for newly created shipments not yet fully synced.
   *
   * @param {Object} params
   * @param {Object} params.countsByShipmentId - Map of shipmentId → counts object
   *   where counts is { batchId: { quantity: number } }
   * @returns {Promise<Array<{ shipment_id: string, total_volume: string }>>}
   */
  async calculateVolumeFromCounts ({ countsByShipmentId, preservePrecision = false } = {}) {
    if (!countsByShipmentId || Object.keys(countsByShipmentId).length === 0) {
      return []
    }

    const allBatchIds = []
    for (const counts of Object.values(countsByShipmentId)) {
      allBatchIds.push(...Object.keys(counts || {}))
    }

    let manufacturerProductIdByBatchId = {}
    try {
      const resolver = createBatchProductResolver({
        fetchBatchesByIds: (ids) => listBatches(this.state, { ids })
      })
      manufacturerProductIdByBatchId = await resolver.resolveMany({ batchIds: allBatchIds })
    } catch (e) {
      manufacturerProductIdByBatchId = {}
    }

    // Collect unique product IDs needed: manufacturer-specific and generic SKU
    const productIds = new Set()
    for (const counts of Object.values(countsByShipmentId)) {
      for (const batchId of Object.keys(counts || {})) {
        const genericProductId = batchId.split(':').slice(0, 2).join(':')
        productIds.add(genericProductId)
        const manufacturerProductId = manufacturerProductIdByBatchId[batchId]
        if (manufacturerProductId) {
          productIds.add(manufacturerProductId)
        }
      }
    }

    const unitVolumes = await this.dataShipmentAdapter.getUnitVolumesForProductIds([...productIds])

    return Object.entries(countsByShipmentId).map(([shipmentId, counts]) => {
      let total = 0
      for (const [batchId, batchData] of Object.entries(counts || {})) {
        const quantity = (batchData && batchData.quantity) || 0
        if (!quantity) continue

        const genericProductId = batchId.split(':').slice(0, 2).join(':')
        const manufacturerProductId = manufacturerProductIdByBatchId[batchId]

        const unitVolume =
          (manufacturerProductId && unitVolumes[manufacturerProductId]) ||
          unitVolumes[genericProductId] ||
          0

        total += quantity * unitVolume
      }
      return {
        shipment_id: shipmentId,
        total_volume: preservePrecision
          ? formatVolumeAsFixedPoint(total)
          : String(Math.round(total * 1000000) / 1000000)
      }
    })
  }

  async calculateVolumeAndPrice (params) {
    return calculateShipmentVolumeAndPrice(params, this.mainApi)
  }

  applyUserFiltersIfNeeded ({ filters, applyUserFilters = false }) {
    return applyLMDUserFilters({ filters, applyUserFilters, user: this.user })
  }

  /**
   * Get CouchDB IDs and location IDs for shipment snapshots by route IDs
   * @param {string[]} routeIds - Array of route IDs to filter by
   * @param {object} options - Query options
   * @param {string[]} [options.programIds] - Filter by program IDs
   * @param {string} [options.cutoffDate] - ISO date string - only return snapshots updated after this date
   * @returns {Promise<{couchdbIds: string[], locationIds: string[]}>} Object with couchdbIds and unique locationIds
   */
  async getSnapshotCouchdbIdsByRoutes (routeIds, options = {}) {
    if (!this.shipmentSnapshotAdapter) {
      throw new Error('PostgreSQL connection not available')
    }
    return this.shipmentSnapshotAdapter.getCouchdbIdsByRoutes(routeIds, options)
  }

  /**
   * Get CouchDB IDs for change documents linked to the specified snapshots
   * @param {string[]} snapshotCouchdbIds - Array of snapshot CouchDB IDs
   * @returns {Promise<string[]>} Array of change document CouchDB IDs
   */
  async getSnapshotChangeDocIds (snapshotCouchdbIds) {
    if (!this.shipmentSnapshotAdapter) {
      throw new Error('PostgreSQL connection not available')
    }
    return this.shipmentSnapshotAdapter.getChangeDocIds(snapshotCouchdbIds)
  }
}

function formatVolumeAsFixedPoint (volume) {
  const [coefficient, exponent] = String(volume).toLowerCase().split('e')
  if (exponent === undefined) return coefficient

  const sign = coefficient.startsWith('-') ? '-' : ''
  const unsignedCoefficient = sign ? coefficient.slice(1) : coefficient
  const [integer, fraction = ''] = unsignedCoefficient.split('.')
  const digits = `${integer}${fraction}`
  const decimalIndex = integer.length + Number(exponent)

  if (decimalIndex <= 0) {
    return `${sign}0.${'0'.repeat(-decimalIndex)}${digits}`
  }
  if (decimalIndex >= digits.length) {
    return `${sign}${digits}${'0'.repeat(decimalIndex - digits.length)}`
  }
  return `${sign}${digits.slice(0, decimalIndex)}.${digits.slice(decimalIndex)}`
}

async function updateOrderDocsRouteInCouchDB (orderDb, orderIds, routeId, routeCreatedAt) {
  const MAX_RETRIES = 3
  const orderUpdateResults = await Promise.allSettled(orderIds.map(async orderId => {
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      let doc
      try {
        doc = await orderDb.get(orderId)
      } catch (err) {
        if (err.status === 404) return
        throw err
      }
      const docRouteCreatedAt = resolveRouteCreatedAt(doc, routeId, routeCreatedAt)
      if (doc.routeId === routeId && (doc.routeCreatedAt || null) === docRouteCreatedAt) return
      try {
        await orderDb.put({ ...doc, routeId, routeCreatedAt: docRouteCreatedAt })
        return
      } catch (err) {
        if (err.status === 409 && attempt < MAX_RETRIES - 1) continue
        throw err
      }
    }
  }))

  const orderFailures = []
  orderUpdateResults.forEach((res, idx) => {
    if (res.status === 'rejected') {
      console.error(`Error updating order doc ${orderIds[idx]} routeId in CouchDB:`, res.reason)
      orderFailures.push(orderIds[idx] || 'unknown')
    }
  })

  if (orderFailures.length) {
    throw new Error(`Failed to update routeId on ${orderFailures.length} order doc(s) in CouchDB`)
  }
}

class SurveyApi extends EntityApi {
  constructor (state, restAdapter) {
    const { user, shipmentsDb } = state
    // Heads up: this is the same adapter as shipments api
    const adapter = new ShipmentPouchAdapter(user, shipmentsDb)
    super(adapter)

    // TODO: remove this when all raw methods have been ported
    wrap(rawSurveyMethods, state, setupShipmentApi, null, this)
  }
}

module.exports.ShipmentApi = ShipmentApi
module.exports.SurveyApi = SurveyApi

module.exports.SHIPMENT_TYPE_LAST_MILE_DELIVERY = DataShipmentAdapter.SHIPMENT_TYPE_LAST_MILE_DELIVERY
module.exports.SHIPMENT_TYPE_FROM_WAREHOUSE = DataShipmentAdapter.SHIPMENT_TYPE_FROM_WAREHOUSE
module.exports.SHIPMENT_TYPE_ALL_TYPES = DataShipmentAdapter.SHIPMENT_TYPE_ALL_TYPES
